/**
 * Serialization utilities for converting between Infrastructure objects and YAML/JSON strings
 */

import * as yaml from 'js-yaml';
import { Infrastructure } from '../types/infrastructure';

/**
 * Serialize infrastructure to YAML string
 */
export function serializeToYAML(infrastructure: Infrastructure): string {
  try {
    return yaml.dump(infrastructure, {
      indent: 2,
      lineWidth: 120,
      noRefs: true,
      sortKeys: false
    });
  } catch (error) {
    throw new Error(`Failed to serialize to YAML: ${(error as Error).message}`);
  }
}

/**
 * Serialize infrastructure to JSON string
 */
export function serializeToJSON(infrastructure: Infrastructure): string {
  try {
    return JSON.stringify(infrastructure, null, 2);
  } catch (error) {
    throw new Error(`Failed to serialize to JSON: ${(error as Error).message}`);
  }
}

/**
 * Parse YAML string to infrastructure object
 */
export function parseYAML(yamlString: string): Infrastructure {
  try {
    const data = yaml.load(yamlString);
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid YAML: must be an object');
    }
    return data as Infrastructure;
  } catch (error) {
    throw new Error(`Failed to parse YAML: ${(error as Error).message}`);
  }
}

/**
 * Parse JSON string to infrastructure object
 */
export function parseJSON(jsonString: string): Infrastructure {
  try {
    return JSON.parse(jsonString);
  } catch (error) {
    throw new Error(`Failed to parse JSON: ${(error as Error).message}`);
  }
}

/**
 * Serialize infrastructure to specified format
 */
export function serialize(
  infrastructure: Infrastructure,
  format: 'yaml' | 'json'
): string {
  return format === 'yaml'
    ? serializeToYAML(infrastructure)
    : serializeToJSON(infrastructure);
}

/**
 * Parse infrastructure from specified format
 */
export function parse(
  content: string,
  format: 'yaml' | 'json'
): Infrastructure {
  return format === 'yaml'
    ? parseYAML(content)
    : parseJSON(content);
}
